import { config } from '@/config/reolink';
import * as types from '../mutation-types';
import { valueWithFallbacks } from '@/common/js/utils';

export interface TUserState {
    pageSkip: number;
    user: {
        avatar: {
            source: string;
            url: string;
        };
        email: {
            address: string;
            status: string;
        };
        metadata: Record<string, any>;
        hasPassword: boolean;
        gender: string;
        firstName: string;
        lastName: string;
        nickName: string;
        id: null|number;
        locale: string;
        country: string;
        totp: boolean;
        status: string;
        isStaff: boolean;
        waitTime: number;
        scheduledAt: number;
    };
    status: string;
    showBeta: boolean;
    UnpayOrders: any[];
    creatingPlan: boolean;
    gpdrObject: Record<string, any>;
}

const localCountry = (): string => localStorage.getItem( 'country' ) ?? '';
const isOtherRegion = (): boolean => localCountry() === 'other';

const state: TUserState = {

    pageSkip: 0,

    user: {

        avatar: {

            source: '',
            url: ''

        },
        email: {

            address: '',
            status: ''

        },
        metadata: {},
        hasPassword: false,
        gender: '',
        firstName: '',
        lastName: '',
        nickName: '',
        id: null,
        locale: '',
        country: '',
        totp: false,
        status: '',
        isStaff: false,
        waitTime: 0,
        scheduledAt: 0
    },
    status: '',
    showBeta: false,
    UnpayOrders: [],
    creatingPlan: false,
    gpdrObject: {}
};

const mutations = {

    [ types.LOGIN ]( state: TUserState, user: TUserState['user'] ): void {

        for ( const key in state['user'] ) {

            if ( key === 'nickName' ) {

                state['user'][ key ] = user[ key ];

            } else {

                state['user'][ key ] = user[ key ] || 0;

            }

        }

    },

    [ types.LOGOUT ]( state: TUserState ): void {

        state.user.id = 0;
        state.user.avatar = {

            source: '',
            url: ''

        };

    },

    [ types.PAGESKIP ]( state: TUserState ): void {

        state.pageSkip++;

    },

    [ types.STATUS_CHANGE ]( state: TUserState, status: TUserState['status'] ): void {

        state.status = status;

    },

    [ types.SHOW_BETA ]( state: TUserState, status: TUserState['showBeta'] ): void {

        state.showBeta = status;

    },

    [ types.UnpayOrders ]( state: TUserState, status: TUserState['UnpayOrders'] ): void {

        state.UnpayOrders = [...status];

    },

    [ types.CreatingPlan ]( state: TUserState, status: TUserState['creatingPlan'] ): void {

        state.creatingPlan = status;

    },

    [ types.GDPR_INSTANCE ]( state: TUserState, status: TUserState['gpdrObject'] ): void {

        state.gpdrObject = status;

    }

};

const country = ( state: TUserState ): string => valueWithFallbacks( localCountry(), state.user.country, config.countryData.ipCountry );

const getters = {

    avatarUrl: ( state: TUserState ): TUserState['user']['avatar']['url'] => state.user.avatar.url,
    emailAddress: ( state: TUserState ): TUserState['user']['email']['address'] => state.user.email.address,
    nickName: ( state: TUserState ): TUserState['user']['nickName'] => state.user.nickName,
    userId: ( state: TUserState ): TUserState['user']['id'] => state.user.id,
    pageSkip: ( state: TUserState ): TUserState['pageSkip'] => state.pageSkip,
    country: ( state: TUserState ): TUserState['user']['country'] => country( state ),
    creatingPlan: ( state: TUserState ): TUserState['creatingPlan'] => state.creatingPlan,
    userStatus: ( state: TUserState ): TUserState['status'] => state.status,
    userInfo: ( state: TUserState ): TUserState => state,
    isLogin: ( state: TUserState ): boolean => !!state.user.id,
    isStorageAvailableCountry: ( state: TUserState ): boolean => config.countryData.cloudStorageCountries.includes( country( state )) && !isOtherRegion(),
    isDataAvailableCountry: ( state: TUserState ): boolean => config.countryData.cloudDataCountries.includes( country( state )) && !isOtherRegion(),
    isOtherRegion: ( state: TUserState, getter: typeof getters ): boolean => !getter.isStorageAvailableCountry && !getter.isDataAvailableCountry,

};

export default {

    state,
    mutations,
    getters

};
